import PendingPayment from 'app/src/payments/PendingPayment';
import CCCharge from 'app/src/data/models/CCCharge';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Format from 'app/src/data/Format';

/**
 * Class definition of a swipe payment (i.e. a debit/credit payment transacted with a card reader)
 */
export default class SwipePayment extends PendingPayment {
  /**
   * Constructor
   *
   * @param {number|Decimal|string} amount   The amount of the swipe payment
   * @param {string} swipeData    The raw data of the swipe
   * @param {string|null}  remoteReference The remote reference to use for the payment; if not specified one will
   *                                  be generated
   * @param saleID {Number}   The ID of the sale for the payment
   * */
  constructor(amount = 0, swipeData = '', remoteReference = null, saleID = 0) {
    super(
      amount,
      PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.SWIPE),
      remoteReference,
      saleID
    );

    this.swipeData = swipeData;
  }

  /**
   * Setter for the swipe data.  Creates a CCCharge model if one doesn't already exist
   * and adds it to the sale model.
   *
   * @param {string} swipeData    The raw swipe data
   */
  set swipeData(swipeData) {
    let ccCharge = this._model.getRelation('CCCharge', CCCharge) || new CCCharge();

    ccCharge.set('swipe', btoa(swipeData));

    this._model.setRelation('CCCharge', ccCharge);
  }

  /**
   * Retrieves the swipe data from the model.
   *
   * @returns {*}
   */
  get swipeData() {
    let swipeData = null;
    let ccCharge = this._model.getRelation('CCCharge', CCCharge);
    if (ccCharge) {
      swipeData = atob(ccCharge.get('swipe'));
    }
    return swipeData;
  }

  /**
   * Retrieves the card brand from the model.
   *
   * @returns {string|null}
   */
  get cardBrand() {
    let cardBrand = null;
    let ccCharge = this._model.getRelation('CCCharge', CCCharge);
    if (ccCharge) {
      cardBrand = ccCharge.get('cardBrand');
    }
    return cardBrand;
  }

  /**
   * Retrieves the card type from the model.
   *
   * @returns {string|null}
   */
  get cardType() {
    let cardType = null;
    let ccCharge = this._model.getRelation('CCCharge', CCCharge);
    if (ccCharge) {
      cardType = ccCharge.get('cardType');
    }
    return cardType;
  }

  /**
   * Retrieves the card masked digits from the model.
   *
   * @returns {string|nulls}
   */
  get maskedDigits() {
    let maskedDigits = null;
    let ccCharge = this._model.getRelation('CCCharge', CCCharge);

    if (this.swipeData) {
      // retrieve the masked digits from the swipe data.  Once the payment service handles
      // swipe transactions we'll likely refactor how this works so we'll just use the existing
      // parsing function for now
      maskedDigits = window.merchantos.register.credit_card.parseSwipe(this.swipeData);
    } else if (ccCharge) {
      maskedDigits = ccCharge.get('xnum');
    }

    return maskedDigits;
  }

  /**
   * Retrieves the details of the payment.  Useful for abstracting payment
   * terminal information irrespective of whether the data is coming from
   * a CCCharge or swipe data.  This method is intended to be compatible with
   * the TerminalPayment method of the same name.
   *
   * @returns {Object}
   * @returns {string} Object.cardBrand    The card brand of the payment
   * @returns {string} Object.cardType    The card type of the payment
   * @returns {string} Object.maskedDigits    The masked digits of the card number
   * @returns {boolean} Object.isDebit    True if the card is a debit card
   * @returns {string}   Object.transactionDate  The date of the transaction
   */
  get details() {
    let details = {};

    if (this._model.has('CCCharge')) {
      details.cardBrand = this.cardBrand;
      details.cardType = this.cardType;
      details.maskedDigits = this.maskedDigits;
      details.isDebit = Format.parseBoolean(this._model.get('CCCharge').isDebit);
      details.transactionDate = Format.parseDate(this._model.get('CCCharge').timeStamp);
    }

    return details;
  }
}
